import { useEffect, useState } from "react";
import { API_BASE } from "../api/config";
import { useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext"; // ✅ get branch & username

function BillPrint() {
  const params = useParams();
  const { username, branch } = useAuth(); // ✅ branch + username from context

  const transaction_number =
    params.transaction_number ||
    params.id ||
    params.txn ||
    params.transactionNumber ||
    "";

  const [billData, setBillData] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [footerText, setFooterText] = useState("");
  const [branchDetails, setBranchDetails] = useState(null); // ✅ store branch details

  // Fetch Bill Data
  useEffect(() => {
    if (!transaction_number) {
      setErrorMsg("Transaction number missing in URL.");
      setLoading(false);
      return;
    }

    const fullUrl = `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get_bill_details_by_transaction_number&transaction_number=${transaction_number}`;

    fetch(fullUrl)
      .then((res) => res.json())
      .then((data) => {
        const data1 = data?.data1 || data?.data || null;
        const data2 = data?.data2 ?? data?.items ?? data?.kot_items ?? null;

        let normalizedItems = [];
        if (Array.isArray(data2)) {
          normalizedItems = data2;
        } else if (data2 && typeof data2 === "object") {
          normalizedItems = Object.values(data2);
        } else if (typeof data2 === "string") {
          try {
            const parsed = JSON.parse(data2);
            if (Array.isArray(parsed)) normalizedItems = parsed;
            else if (parsed && typeof parsed === "object")
              normalizedItems = Object.values(parsed);
          } catch (e) {
            console.warn("Could not parse data2 string:", e);
          }
        }

        normalizedItems = normalizedItems.map((it) => ({
          ...it,
          item_qty: Number(it?.item_qty || 0),
          item_price: Number(it?.item_price || 0),
          item_taxable_price: Number(it?.item_taxable_price || it?.item_price || 0),
        }));

        setBillData({
          raw: data,
          data1: Array.isArray(data1) ? data1 : data1 ? [data1] : [],
        });
        setItems(normalizedItems);

        if (
          (Array.isArray(data1) && data1.length === 0) &&
          normalizedItems.length === 0
        ) {
          setErrorMsg("No bill data found for this transaction.");
        } else {
          setErrorMsg("");
        }
      })
      .catch((err) => {
        console.error("🔥 Error fetching bill details:", err);
        setErrorMsg("Error fetching bill details.");
      })
      .finally(() => setLoading(false));
  }, [transaction_number]);

  // Fetch Company Footer Data
  useEffect(() => {
    fetch(`${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get&table=company`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.status === "ok" && data?.data?.length > 0) {
          setFooterText(data.data[0].terms_and_conditions || "");
        }
      })
      .catch((err) => console.error("❌ Error fetching company footer:", err));
  }, []);

  // ✅ Fetch Branch Details
  useEffect(() => {
    fetch(
      `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get&table=branches&condition=get_branch_det&branch=${branch}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setBranchDetails(data[0]); // ✅ save entire branch object
        }
      })
      .catch((err) => console.error("Error fetching branch details:", err));
  }, [branch]);

  const company = (billData && billData.data1 && billData.data1[0]) || {};
  const isCancelled = Number(company.cancel_status) === 1; // ✅ check if bill cancelled

  // ✅ GST condition check
  const gstEnabled = branchDetails?.gst_bill === "Yes";

  // ✅ compute totals based on GST condition
  const subtotal = Number(company.subtotal || 0);
  const discountAmount = Number(company.invoice_disc_amt || 0);
  const couponDiscount = Number(company.coupon_discount || 0);
  const additionalCharges = Number(company.invoice_additional_charges || 0);
  const invoice_taxable = gstEnabled ? Number(company.invoice_taxable || 0) : 0;
  const invoice_tax_amt = gstEnabled ? Number(company.invoice_tax_amt || 0) : 0;
  const grandTotal = Number(company.invoice_value || 0);
  const adjust_amount = Number(company.adjust_amount || 0);
  const cash = Number(company.cash || 0);
  const online_cash = Number(company.online_cash || 0);
  const bill_reference = company.bill_reference;

  // ✅ Format function for footer datetime
  const formatDateTime = (date) => {
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, "0");
    const mi = String(date.getMinutes()).padStart(2, "0");
    const ss = String(date.getSeconds()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy} ${hh}:${mi}:${ss}`;
  };

  // ✅ Print Function
  const handlePrint = () => {
    const isCancelled = Number(company.cancel_status) === 1;

    const htmlContent = `
      <div style="font-family: monospace, sans-serif; padding: 10px; width: 280px; color: #000; position: relative;">
        ${
          isCancelled
            ? `<div style="
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) rotate(-30deg);
                font-size: 42px;
                font-weight: bold;
                color: rgba(255,0,0,0.2);
                text-transform: uppercase;
                pointer-events: none;
                z-index: 0;
              ">CANCELLED</div>`
            : ""
        }
        <div style="position: relative; z-index: 1;">
          <h2 style="text-align: center; margin: 5px 0;">${branchDetails?.name || "Restaurant"}</h2>
          <p style="text-align: center; font-size: 12px; margin-bottom: 8px;">
            ${branchDetails?.address || ""}
          </p>

          <h3 style="text-align: center; margin: 5px 0;">Final Bill</h3>
          <p><b>Order :</b> ${transaction_number}</p>
          <p><b>Invoice Date:</b> ${
      company.invoice_date
        ? (() => {
            const d = new Date(company.invoice_date);
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const dd = String(d.getDate()).padStart(2, "0");
            const yyyy = d.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
          })()
        : "-"
    } ${company.invoice_time || ""}</p>

          ${
            company.customer_name ? `<p><b>Name:</b> ${company.customer_name}</p>` : ""
          }
          ${
            company.customer_mobile ? `<p><b>Mobile:</b> ${company.customer_mobile}</p>` : ""
          }
          ${
            company.gstin_number ? `<p><b>GSTIN:</b> ${company.gstin_number}</p>` : ""
          }

          <table style="width:100%; border-collapse: collapse; margin-top: 8px; font-size: 13px;" border="1">
            <thead><tr><th>Item</th><th>Qty</th><th>Total</th></tr></thead>
            <tbody>
              ${
                items.length > 0
                  ? items.map(
                      (i) =>
                        `<tr>
                          <td style="font-size: 18px; font-weight: bold;">${i.item_name}</td>
                          <td style="font-size: 18px; font-weight: bold;text-align:center;">${i.item_qty}</td>
                          <td style="font-size: 18px; font-weight: bold;text-align:right;">₹${(
                            i.item_qty *
                            (gstEnabled ? i.item_taxable_price : i.item_price)
                          ).toFixed(2)}</td>
                        </tr>`
                    ).join("")
                  : `<tr><td colspan="3" style="text-align:center;">No items found</td></tr>`
              }
            </tbody>
          </table>

          <div style="margin-top: 8px; font-size: 13px;text-align: right;">
            ${gstEnabled ? `<p>Taxable: ₹${invoice_taxable.toFixed(2)}</p>
            <p>Tax(Cgst-2.5%,Sgst-2.5%): ₹${invoice_tax_amt.toFixed(2)}</p>` : ""}
            <p>Subtotal: ₹${subtotal.toFixed(2)}</p>
            ${discountAmount !== 0 ? `<p>Discount: -₹${discountAmount.toFixed(2)}</p>` : ""}
            ${couponDiscount !== 0 ? `<p>Coupon: -₹${couponDiscount.toFixed(2)}</p>` : ""}
            ${additionalCharges !== 0 ? `<p>Addition: +₹${additionalCharges.toFixed(2)}</p>` : ""}
            ${adjust_amount !== 0 ? `<p>Round Off: ₹${adjust_amount.toFixed(2)}</p>` : ""}
            <p><b>Grand Total: ₹${grandTotal.toFixed(2)}</b></p>
            <p>Payment Mode: ${company.payment_mode || "-"}</p>
            ${cash !== 0 ? `<p>Cash Paid: ₹${cash.toFixed(2)}</p>` : ""}
            ${online_cash !== 0 ? `<p>Online Paid: ₹${online_cash.toFixed(2)}</p>` : ""}
            ${
              bill_reference !== 0 && bill_reference !== ""
                ? `<p>Online Ref No: ${bill_reference}</p>`
                : ""
            }
          </div>

          <div style="text-align: center; margin-top: 10px; font-size: 12px;">
            ✅ Thank you for dining with us!
            <br/>
            ${
              footerText
                ? `<div style="margin-top:6px; font-size:11px;">${footerText}</div>`
                : ""
            }
          </div>

          <div style="text-align: center; margin-top: 10px; font-size: 11px; border-top: 1px dashed #333; padding-top: 6px;">
            Generated on ${formatDateTime(new Date())} by ${username} (${branch})
          </div>
        </div>
      </div>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();

    printWindow.onafterprint = () => {
      printWindow.close();
    };
    setTimeout(() => {
      try {
        printWindow.close();
      } catch (e) {}
    }, 2000);
  };

  const handleClose = () => window.close();

  if (loading) return <div className="p-6 text-center">Loading Bill Details...</div>;
  if (errorMsg)
    return (
      <div className="p-6 text-center text-red-600">
        {errorMsg}
        <div style={{ marginTop: 12, fontSize: 12, color: "#444" }}>
          Check browser console for raw API response & params.
        </div>
      </div>
    );

  return (
    <div className="min-h-screen p-6 bg-white text-black">
      <div
        style={{
          maxWidth: 480,
          margin: "0 auto",
          padding: 20,
          border: "1px solid #ddd",
          borderRadius: 8,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* ✅ CANCELLED WATERMARK */}
        {isCancelled && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%) rotate(-30deg)",
              fontSize: 64,
              fontWeight: "bold",
              color: "rgba(255, 0, 0, 0.15)",
              textTransform: "uppercase",
              pointerEvents: "none",
              zIndex: 0,
              whiteSpace: "nowrap",
            }}
          >
            CANCELLED
          </div>
        )}

        {/* ✅ Bill Content */}
        <div style={{ position: "relative", zIndex: 1 }}>
          <h2 style={{ textAlign: "center" }}>{branchDetails?.name || "Restaurant"}</h2>
          {branchDetails?.address && (
            <p style={{ textAlign: "center", fontSize: 12, color: "#555" }}>
              {branchDetails.address}
            </p>
          )}
          <h3 style={{ textAlign: "center", marginTop: 8 }}>
            Order - {transaction_number}
          </h3>

          <p style={{ textAlign: "center", fontSize: 12, marginTop: 4 }}>
            <b>Invoice Date:</b>{" "}
            {company.invoice_date
              ? (() => {
                  const d = new Date(company.invoice_date);
                  const dd = String(d.getDate()).padStart(2, "0");
                  const mm = String(d.getMonth() + 1).padStart(2, "0");
                  const yyyy = d.getFullYear();
                  return `${dd}/${mm}/${yyyy}`;
                })()
              : "-"}{" "}
            {company.invoice_time || ""}
          </p>

          {/* Customer Details */}
          <div
            style={{
              background: "#f7f7f7",
              padding: 12,
              borderRadius: 6,
              marginTop: 12,
            }}
          >
            {company.customer_name && <div><b>Name:</b> {company.customer_name}</div>}
            {company.customer_mobile && <div><b>Mobile:</b> {company.customer_mobile}</div>}
            {company.gstin_number && <div><b>GSTIN:</b> {company.gstin_number}</div>}
          </div>

          {/* Items Table */}
          <div style={{ overflowX: "auto", marginTop: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ background: "#eee" }}>
                <tr>
                  <th style={{ padding: 6, textAlign: "left" }}>Item</th>
                  <th style={{ padding: 6, textAlign: "center" }}>Qty</th>
                  <th style={{ padding: 6, textAlign: "right" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ padding: 10, textAlign: "center" }}>
                      No items found
                    </td>
                  </tr>
                ) : (
                  items.map((i, idx) => (
                    <tr key={idx} style={{ borderTop: "1px solid #eee" }}>
                      <td style={{ padding: 6 }}>{i.item_name || "-"}</td>
                      <td style={{ padding: 6, textAlign: "center" }}>{i.item_qty}</td>
                      <td style={{ padding: 6, textAlign: "right" }}>
                        ₹
                        {(
                          i.item_qty *
                          (gstEnabled ? i.item_taxable_price : i.item_price)
                        ).toFixed(2)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div style={{ textAlign: "right", marginTop: 12 }}>
            {gstEnabled && (
              <>
                <p>Taxable: ₹{invoice_taxable.toFixed(2)}</p>
                <p>Tax(Cgst-2.5%,Sgst-2.5%): ₹{invoice_tax_amt.toFixed(2)}</p>
              </>
            )}
            <div>Subtotal: ₹{subtotal.toFixed(2)}</div>
            {discountAmount !== 0 && (
              <div>Discount: -₹{discountAmount.toFixed(2)}</div>
            )}
            {couponDiscount !== 0 && (
              <div>Coupon: -₹{couponDiscount.toFixed(2)}</div>
            )}
            {additionalCharges !== 0 && (
              <p>Addition: +₹{additionalCharges.toFixed(2)}</p>
            )}
            {adjust_amount !== 0 && (
              <p>Round Off: ₹{adjust_amount.toFixed(2)}</p>
            )}
            <div style={{ fontWeight: "bold", marginTop: 6 }}>
              Grand Total: ₹{grandTotal.toFixed(2)}
            </div>
            <div style={{ marginTop: 6 }}>
              Payment Mode: {company.payment_mode || "-"}
            </div>
            {cash !== 0 && <p>Cash Paid: ₹{cash.toFixed(2)}</p>}
            {online_cash !== 0 && <p>Online Paid: ₹{online_cash.toFixed(2)}</p>}
            {bill_reference !== 0 && bill_reference !== "" && (
              <p>Online Ref No: {bill_reference}</p>
            )}
          </div>

          {/* Footer */}
          {footerText && (
            <div
              style={{
                textAlign: "center",
                marginTop: 16,
                fontSize: 12,
                color: "#555",
              }}
            >
              {footerText}
            </div>
          )}

          <div style={{ textAlign: "center", marginTop: 12 }}>
            <p style={{ fontSize: 13, fontWeight: "500" }}>
              ✅ Thank you for dining with us!
            </p>
            <p style={{ fontSize: 11, color: "#777", marginTop: 4 }}>
              Generated on {formatDateTime(new Date())} by {username} ({branch})
            </p>
          </div>

          {/* Buttons */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: 16, gap: 8 }}>
            <button
              onClick={handlePrint}
              style={{
                backgroundColor: "#007bff",
                color: "#fff",
                padding: "6px 16px",
                borderRadius: 4,
                border: "none",
              }}
            >
              🖨 Print
            </button>
            <button
              onClick={handleClose}
              style={{
                backgroundColor: "#555",
                color: "#fff",
                padding: "6px 16px",
                borderRadius: 4,
                border: "none",
              }}
            >
              ✖ Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BillPrint;
